<!-- ConsoleLogs.vue -->
<template>
  <div class="console-logs" :class="{ 'fixed-bottom': fixed }" v-show="visible">
    <div class="console-header">
      <h4>Console Output</h4>
      <div class="console-controls">
        <button @click="toggleVisibility" class="toggle-btn">
          {{ visible ? 'Hide' : 'Show' }}
        </button>
        <button @click="clearLogs" class="clear-btn">Clear</button>
      </div>
    </div>

    <div class="console-body" ref="body" :style="{ maxHeight: maxHeight + 'px' }">
      <pre v-for="(log, index) in logs" :key="index" :class="['log-line', log.type]">{{ log.message }}</pre>
      <div v-if="logs.length === 0" class="empty">No logs yet...</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ConsoleLogs',

  props: {
    fixed: {
      type: Boolean,
      default: true,
    },
    maxHeight: {
      type: Number,
      default: 300,
    },
  },

  data() {
    return {
      logs: [],
      visible: true,
      originalConsole: {},
      maxLogs: 200,
    }
  },

  mounted() {
    // Save original console methods
    this.originalConsole.log = console.log.bind(console)
    this.originalConsole.warn = console.warn.bind(console)
    this.originalConsole.error = console.error.bind(console)

    // Override console methods
    console.log = (...args) => {
      this.addLog('log', args)
      this.originalConsole.log(...args)
    }
    console.warn = (...args) => {
      this.addLog('warn', args)
      this.originalConsole.warn(...args)
    }
    console.error = (...args) => {
      this.addLog('error', args)
      this.originalConsole.error(...args)
    }

    // Capture global uncaught errors (the red ones in dev tools)
    window.onerror = (message, source, lineno, colno, error) => {
      this.addLog(
        'error',
        [`Uncaught Error: ${message}`, `at ${source}:${lineno}:${colno}`, error ? error.stack : ''].filter(Boolean)
      )
      return false // Let browser still log it too
    }

    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason
      let message = 'Unhandled Promise Rejection'

      if (reason) {
        if (reason instanceof Error) {
          message += `: ${reason.message}\n${reason.stack || ''}`
        } else {
          message += `: ${String(reason)}`
        }
      }

      this.addLog('error', [message])
    })

    // Auto-scroll to bottom when new logs arrive
    this.$watch('logs', () => {
      this.$nextTick(() => {
        const body = this.$refs.body
        if (body) body.scrollTop = body.scrollHeight
      })
    })
  },

  beforeDestroy() {
    // Restore original console methods
    if (this.originalConsole.log) console.log = this.originalConsole.log
    if (this.originalConsole.warn) console.warn = this.originalConsole.warn
    if (this.originalConsole.error) console.error = this.originalConsole.error

    // Clean up global error handlers
    window.onerror = null
    // unhandledrejection listener is automatically cleaned on component destroy
  },

  methods: {
    addLog(type, args) {
      const message = args.map((arg) => this.formatArg(arg)).join(' ')
      this.logs.push({ type, message })
      if (this.logs.length > this.maxLogs) this.logs.shift()
    },

    formatArg(arg) {
      if (arg === null) return 'null'
      if (arg === undefined) return 'undefined'
      if (typeof arg === 'string') return arg
      if (typeof arg === 'number' || typeof arg === 'boolean') return String(arg)
      try {
        return JSON.stringify(arg, null, 2)
      } catch (e) {
        return String(arg)
      }
    },

    clearLogs() {
      this.logs = []
    },

    toggleVisibility() {
      this.visible = !this.visible
    },
  },
}
</script>

<style scoped>
.console-logs {
  background: #1e1e1e;
  color: #fff;
  border-radius: 8px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  flex-direction: column;

  /* Responsive width – never wider than screen */
  width: min(420px, 95vw);
  max-width: 100vw;
  box-sizing: border-box;
}

.console-logs.fixed-bottom {
  position: fixed;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  right: auto;
}

@media (min-width: 500px) {
  .console-logs.fixed-bottom {
    left: auto;
    right: 10px;
    transform: none;
  }
}

.console-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #333;
  border-radius: 8px 8px 0 0;
  flex-shrink: 0;
}

.console-header h4 {
  margin: 0;
  font-size: 14px;
}

.console-controls button {
  background: #444;
  color: #fff;
  border: none;
  padding: 4px 8px;
  margin-left: 6px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.console-controls button:hover {
  background: #555;
}

.console-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: auto; /* fallback for very long lines */
  padding: 10px 12px;
  background: #1e1e1e;
  border-radius: 0 0 8px 8px;
}

.log-line {
  margin: 3px 0;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.log-line.log {
  color: #ddd;
}
.log-line.warn {
  color: #ffcc00;
}
.log-line.error {
  color: #ff5555;
}

.empty {
  color: #666;
  font-style: italic;
  text-align: center;
  padding: 20px 0;
}
</style>
